// Pure conversation hydration / persistence helpers for the fullscreen
// chat widget (`fullscreen-chat.tsx`).
//
// These encode the privacy-sensitive rule that distinguishes PUBLIC bots
// from AUTHENTICATED ones:
//
//   - PUBLIC bots (accessMode !== "authenticated") never restore prior
//     threads from the browser. Every visit/refresh starts fresh and we
//     proactively clear any history left in localStorage so a stale public
//     thread can never linger (a privacy/UX leak). Public bots also never
//     write their thread list back to the browser.
//   - AUTHENTICATED bots resume: their stored thread list is parsed,
//     Date fields are revived, the previously active conversation is
//     re-selected, and the running thread list is persisted on change.
//
// The logic lives here (instead of inline in the component effects) so it
// can be unit-tested against an in-memory storage stub without mounting
// the full React tree. See `__tests__/fullscreen-chat.hydration.test.ts`.

export interface ChatMessage {
  citations?: import("@/components/knowledge-citations").KnowledgeCitation[];
  id: string;
  role: "user" | "assistant" | "agent" | "system";
  content: string;
  timestamp: Date;
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentMime?: string;
}

export interface ChatConversation {
  id: string;
  title: string;
  messages: ChatMessage[];
  sessionId: string;
  backendConversationId: string | null;
  createdAt: Date;
}

// Minimal subset of the Web Storage API the helpers depend on, so tests
// can pass an in-memory stub instead of relying on jsdom/localStorage.
export interface ChatStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface HydratedChatState {
  conversations: ChatConversation[];
  // The conversation that should be shown on load (saved active one, or
  // the first thread as a fallback). Null when there's nothing to restore.
  active: ChatConversation | null;
}

const EMPTY: HydratedChatState = { conversations: [], active: null };

// A bot is "public" for hydration purposes whenever its access mode is
// anything other than the explicit "authenticated" value. Undefined /
// unknown modes are treated as public (fail-closed for privacy: never
// resurface a stored thread unless we're certain the bot is gated).
export function isPublicAccessMode(accessMode: string | undefined | null): boolean {
  return accessMode !== "authenticated";
}

// Decide what conversations to load into memory for a given bot.
//
// Public bots: clear any stored history and return empty state.
// Authenticated bots: parse + revive the stored thread list and pick the
// active conversation. Corrupt / empty storage falls back to empty state.
export function hydrateChatState(
  storage: ChatStorage,
  storageKey: string,
  isPublic: boolean,
): HydratedChatState {
  if (isPublic) {
    // Discard any history left in the browser from a previous session so
    // refreshes and return visits always begin with a clean conversation.
    try {
      storage.removeItem(storageKey);
    } catch {
      // Storage disabled — nothing to clear.
    }
    return EMPTY;
  }
  try {
    const raw = storage.getItem(storageKey);
    if (raw) {
      const saved = JSON.parse(raw) as {
        conversations: ChatConversation[];
        activeConvoId: string | null;
      };
      if (Array.isArray(saved.conversations) && saved.conversations.length > 0) {
        // Re-hydrate Date fields (JSON.parse gives strings back).
        const restored: ChatConversation[] = saved.conversations.map((c) => ({
          ...c,
          createdAt: new Date(c.createdAt),
          messages: (c.messages || []).map((m) => ({
            ...m,
            timestamp: new Date(m.timestamp),
          })),
        }));
        const active =
          restored.find((c) => c.id === saved.activeConvoId) || restored[0] || null;
        return { conversations: restored, active };
      }
    }
  } catch {
    // Corrupt storage — ignore and start fresh.
  }
  return EMPTY;
}

// Persist the running thread list, returning whether anything was written.
//
// Public bots never persist (returns false): writing history back would
// re-create the stale-thread problem on the next load.
export function persistChatState(
  storage: ChatStorage,
  storageKey: string,
  isPublic: boolean,
  conversations: ChatConversation[],
  activeConvoId: string | null,
): boolean {
  if (isPublic) return false;
  try {
    storage.setItem(storageKey, JSON.stringify({ conversations, activeConvoId }));
    return true;
  } catch {
    // Quota exceeded or storage disabled — fail silently.
    return false;
  }
}
